import { Header } from '@/components/layout/Header';
import { Footer } from '@/components/layout/Footer';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { ArrowRight, LogIn, Users } from 'lucide-react';
import { resolveAppKgobBase } from '@/lib/getYourAnswerApi';

const portals = [
  {
    title: "I'm an Existing Client",
    tagline: 'Sign in to your client portal.',
    desc: 'Access documents, deliverables, and the status of your work.',
    icon: LogIn,
    path: '/portal/login',
    testid: 'signin-client-portal-card',
  },
  {
    title: "I'm an Employee",
    tagline: 'Sign in to your KGOB workspace.',
    desc: 'Access the team CRM, client work, and tools.',
    icon: Users,
    path: '/login',
    testid: 'signin-employee-card',
  },
];

export default function SignIn() {
  return (
    <div className="min-h-screen bg-[#0B2240]">
      <Helmet>
        <title>Sign In | KGOB CPAs & Advisors</title>
        <meta name="description" content="Sign in to your KGOB client portal or employee workspace." />
        <link rel="canonical" href="https://kgob.com/sign-in" />
        <meta name="robots" content="noindex, follow" />
      </Helmet>

      <Header />

      <main className="bg-[linear-gradient(180deg,#0B2240_0%,#07182F_100%)] text-white noise-overlay">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-center mb-12"
          >
            <p className="uppercase tracking-[0.18em] text-xs text-[#E3C96E] mb-4">Secure Access</p>
            <h1 className="font-['Playfair_Display'] text-4xl sm:text-5xl font-semibold mb-4" data-testid="signin-headline">
              Sign in to KGOB.
            </h1>
            <p className="text-white/75">Choose the workspace that belongs to you.</p>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {portals.map((p, i) => {
              const Icon = p.icon;
              return (
                <motion.a
                  key={p.title}
                  href={`${resolveAppKgobBase()}${p.path}`}
                  initial={{ opacity: 0, y: 14 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: 0.1 + i * 0.08 }}
                  whileHover={{ scale: 1.015 }}
                  className="group relative block rounded-2xl border border-white/15 bg-white/10 p-6 backdrop-blur-xl transition-all duration-300 hover:border-white/30 hover:shadow-[0_12px_40px_rgba(11,34,64,.24)]"
                  data-testid={p.testid}
                >
                  <div className="flex items-start justify-between mb-3">
                    <Icon className="h-7 w-7 text-[#D4AF37]" />
                    <ArrowRight className="h-5 w-5 text-white/60 transition-all duration-200 group-hover:text-white group-hover:translate-x-1" />
                  </div>
                  <h2 className="font-['Playfair_Display'] text-xl font-semibold mb-2 text-white">{p.title}</h2>
                  <p className="text-sm font-medium mb-2 text-[#E3C96E]">{p.tagline}</p>
                  <p className="text-sm leading-relaxed text-white/70">{p.desc}</p>
                </motion.a>
              );
            })}
          </div>

          <p className="text-center text-sm text-white/50 mt-10">
            Trouble signing in? Write to us at{' '}
            <a data-testid="sign-in-a-1" href="mailto:connect@kgob.com" className="text-[#D4AF37] hover:underline">connect@kgob.com</a>{' '}
            or call 1-844-599-3355.
          </p>
        </div>
      </main>

      <Footer />
    </div>
  );
}
